import { useState } from "react";
import { KIND_LABELS, cleanCatalog, editsCatalog, isLmiv } from "@speisekarte/core";
import type { Allergen, AllergenKind } from "@speisekarte/core";
import { api, messageOf, store, useStore } from "../store.ts";
import { useDraft, useKept } from "../kept.ts";
import { OfficialLine, PhraseLine } from "./TextWork.tsx";

const complete = (a: Allergen) => a.code.trim() !== "" && a.name.trim() !== "";

/**
 * „Allergene“: the business's catalogue of allergens and additives (S5). The 14 of the LMIV are
 * fixed; every other change is saved as one leaves the field, a new row once it has code and name
 * (Martin, 01.11.2026: no „Speichern“ that looks as if it were not needed).
 */
export function Allergens() {
  const s = useStore();
  const data = s.data!;
  const canEdit = editsCatalog(s.role);
  const [rows, setRows] = useDraft<Allergen[]>("allergens", JSON.stringify(data.allergens), () => data.allergens.map((a) => ({ ...a })));
  // New rows apart: they are not part of the saved list until complete.
  const [fresh, setFresh] = useKept<Allergen[]>("allergens-new", []);
  const [state, setState] = useState<{ ok?: string; error?: string }>({});
  const used = new Map<string, number>();
  for (const d of data.dishes) for (const c of d.allergens ?? []) used.set(c, (used.get(c) ?? 0) + 1);

  async function save(next: Allergen[], pending: Allergen[]) {
    const all = [...next, ...pending.filter(complete)];
    // Allergens before additives, as the codes of a dish are sorted.
    const list = [...all.filter((a) => a.kind === "allergen"), ...all.filter((a) => a.kind === "additive")];
    if (JSON.stringify(list) === JSON.stringify(data.allergens)) return setFresh(pending);
    const clean = cleanCatalog(list);
    if ("error" in clean) return setState({ error: clean.error });
    try {
      await store.change(() => api("/api/allergens", { method: "PUT", body: { allergens: clean.value } }));
      setFresh(pending.filter((a) => !complete(a)));
      setState({ ok: "Gespeichert" });
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  const change = (i: number, c: Partial<Allergen>) => [setRows(rows.map((r, k) => (k === i ? { ...r, ...c } : r))), setState({})];
  const changeFresh = (i: number, c: Partial<Allergen>) => [setFresh(fresh.map((r, k) => (k === i ? { ...r, ...c } : r))), setState({})];

  const fixed = (a: Allergen) => (
    <tr key={a.code} className="lmiv" data-testid={`allergen-${a.code}`}>
      <td className="code">
        <span>{a.code}</span>
      </td>
      <td>
        <span>{a.name}</span>
        <OfficialLine allergen={a} data={data} />
      </td>
      <td className="kind">
        <span className="muted small">LMIV</span>
      </td>
      <td className="act">
        {used.get(a.code) ? (
          <span className="muted small" title="Steht bei Speisen">
            {used.get(a.code)}×
          </span>
        ) : null}
      </td>
    </tr>
  );

  /** A row of the business's own: saved or new (`isNew`), each change saved as it is left. */
  const own = (a: Allergen, i: number, isNew: boolean) => {
    const set = isNew ? (c: Partial<Allergen>) => changeFresh(i, c) : (c: Partial<Allergen>) => change(i, c);
    const leave = () => void save(rows, fresh);
    const kind = (k: AllergenKind) => (isNew ? save(rows, fresh.map((r, j) => (j === i ? { ...r, kind: k } : r))) : save(rows.map((r, j) => (j === i ? { ...r, kind: k } : r)), fresh));
    const remove = () => (isNew ? setFresh(fresh.filter((_, k) => k !== i)) : save(rows.filter((_, k) => k !== i), fresh));
    return (
      <tr key={isNew ? `new-${i}` : `row-${i}`} className={isNew ? "unsaved" : undefined} data-testid={isNew ? "allergen-new" : `allergen-${a.code}`}>
        <td className="code">
          <input value={a.code} onChange={(e) => set({ code: e.target.value })} onBlur={leave} readOnly={!canEdit} aria-label="Kürzel" placeholder={isNew ? "Kürzel" : undefined} />
        </td>
        <td>
          <input value={a.name} onChange={(e) => set({ name: e.target.value })} onBlur={leave} readOnly={!canEdit} aria-label="Bezeichnung" placeholder={isNew ? "Bezeichnung" : undefined} />
          {/* Its translation right here (S61 § 4.1), once saved under this name. */}
          {!isNew && data.allergens.some((x) => x.code === a.code && x.name === a.name) && <PhraseLine text={a.name} data={data} />}
        </td>
        <td className="kind">
          <select value={a.kind} onChange={(e) => [set({ kind: e.target.value as AllergenKind }), void kind(e.target.value as AllergenKind)]} disabled={!canEdit} aria-label="Art">
            <option value="allergen">{KIND_LABELS.allergen}</option>
            <option value="additive">{KIND_LABELS.additive}</option>
          </select>
        </td>
        <td className="act">
          {canEdit &&
            (!isNew && used.get(a.code) ? (
              <span className="muted small" title="Steht bei Speisen">
                {used.get(a.code)}×
              </span>
            ) : (
              <button className="link danger" onClick={() => void remove()} aria-label={`${a.code || "Neue Zeile"} entfernen`}>
                ✕
              </button>
            ))}
        </td>
      </tr>
    );
  };

  const table = (kind: AllergenKind) => (
    <table className="allergen-table">
      <tbody>
        {rows.map((a, i) => (a.kind !== kind ? null : isLmiv(a.code) ? fixed(a) : own(a, i, false)))}
        {fresh.map((a, i) => (a.kind !== kind ? null : own(a, i, true)))}
      </tbody>
    </table>
  );

  return (
    <div>
      <p>
        <a className="back" href="#/kataloge">← Kataloge</a>
      </p>
      <section className="card">
        <h2>Allergene</h2>
        <p className="hint">Die 14 Hauptallergene der Lebensmittelinformationsverordnung, wie auf Speisekarten üblich mit Buchstaben; sie stehen fest. Eigene kommen darunter dazu, etwa „a1 Weizen“ oder „h2 Haselnuss“. Auf der Speisekarte stehen die Kürzel bei der Speise, unten die Legende.</p>
        {table("allergen")}
        <h2>Zusatzstoffe</h2>
        {table("additive")}
        {canEdit && (
          <div className="buttons">
            <button onClick={() => setFresh([...fresh, { code: "", name: "", kind: "additive" }])}>+ Zusatzstoff</button>
            <button onClick={() => setFresh([...fresh, { code: "", name: "", kind: "allergen" }])}>+ Allergen</button>
          </div>
        )}
        {/* A new row is only in this app until it has code and name: said plainly, so it does not look saved (Martin, 01.11.2026). */}
        {fresh.length > 0 && (
          <p className="warn" data-testid="allergens-unsaved">
            {fresh.length === 1 ? "Eine neue Zeile ist" : `${fresh.length} neue Zeilen sind`} noch nicht gespeichert: Kürzel und Bezeichnung ausfüllen.
          </p>
        )}
        {state.error && <p className="error-box">{state.error}</p>}
        {state.ok && <p className="ok-text">{state.ok}</p>}
        <p className="hint">Jede Änderung wird gespeichert, sobald du das Feld verlässt; eine neue Zeile, sobald Kürzel und Bezeichnung ausgefüllt sind. Ein Kürzel, das bei Speisen steht (Zahl rechts), lässt sich erst entfernen, wenn es dort entfernt ist. Die Nummern der Zusatzstoffe bitte mit der eigenen Liste abgleichen.</p>
      </section>
    </div>
  );
}
